import reference from '../data/validation-reference-2025.json' with { type: 'json' };
import type { FacilityMeta } from './tier2submit-export.ts';

// Coordinates are microdegrees in the versioned Tier2 Submit county polygons.
// Each zero-delimited source ring is retained; even/odd containment handles holes.
function polygonContains(rings: number[][][], longitude: number, latitude: number): boolean {
  const x = longitude * 1e6;
  const y = latitude * 1e6;
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[j]!;
      const b = ring[i]!;
      const ax = a[0]!,
        ay = a[1]!,
        bx = b[0]!,
        by = b[1]!;
      const cross = (x - ax) * (by - ay) - (y - ay) * (bx - ax);
      if (
        Math.abs(cross) <= 0.01 &&
        x >= Math.min(ax, bx) &&
        x <= Math.max(ax, bx) &&
        y >= Math.min(ay, by) &&
        y <= Math.max(ay, by)
      )
        return true;
      if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
    }
  }
  return inside;
}
function countyNameKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+(county|parish|municipio|borough|census area|municipality)$/i, '')
    .replace(/[.]/g, '')
    .replace(/\s+/g, ' ');
}
export function facilityGeographyIssues(
  meta: Pick<FacilityMeta, 'state' | 'county' | 'city' | 'latitude' | 'longitude'>,
): string[] {
  const { latitude, longitude } = meta;
  if (latitude === null || longitude === null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
  const counties = reference.counties.filter((county) => county.state === meta.state);
  if (!counties.length) return [];
  const issues: string[] = [];
  const positive = meta.state === 'GU' || meta.state === 'MP';
  const aleutians = meta.state === 'AK' && longitude > 0;
  if (positive && longitude < 0) issues.push('Longitude must be positive for ' + meta.state + '.');
  if (!positive && !(aleutians && /^(shemya|simiya) island$/i.test(meta.city.trim())) && longitude >= 0)
    issues.push('Longitude must be negative for ' + meta.state + '.');
  const x = aleutians ? longitude - 360 : longitude;
  const contains = (county: (typeof counties)[number]) =>
    x >= county.bounds[0]! &&
    x <= county.bounds[2]! &&
    latitude >= county.bounds[1]! &&
    latitude <= county.bounds[3]! &&
    polygonContains(county.rings, x, latitude);
  if (!counties.some(contains)) issues.push('The coordinates fall outside the selected state (' + meta.state + ').');
  else if (meta.county.trim()) {
    const selected = counties.filter((county) => countyNameKey(county.name) === countyNameKey(meta.county));
    if (!selected.length) issues.push('Select a recognized county or planning region for ' + meta.state + '.');
    else if (!selected.some(contains))
      issues.push('The coordinates fall outside the selected county or planning region (' + meta.county + ').');
  }
  return issues;
}
export const tier2SubmitEhsCas = new Set(reference.ehs);
